import { isSafeRelativeArchivePath } from "./archivePaths";

const MB = 1024 * 1024;

export const BROWSER_IMPORT_LIMITS = {
  meshFileBytes: 50 * MB,
  archiveFileBytes: 250 * MB,
  archiveEntryBytes: 100 * MB,
  archiveTotalUncompressedBytes: 512 * MB,
  archiveEntries: 1500,
  objTextBytes: 50 * MB,
  objVertexLines: 1_000_000,
  objFaceLines: 1_000_000,
  objMaterialReferenceLines: 250_000,
} as const;

export const MAXIMUM_ASSET_SIZE_MESSAGE =
  "You have exceeded the maximum asset size limit for browser import.";

type BrowserImportLimitRejectedAsset = {
  fileName: string;
  actualBytes: number | null;
  actualSizeLabel: string;
};

type BrowserImportLimitIssue = {
  code: string;
  message: string;
  fileName: string;
  actualBytes: number | null;
  limitBytes: number | null;
  actualSizeLabel: string;
  limitSizeLabel: string;
  helpMessage: string;
  rejectedAssets?: BrowserImportLimitRejectedAsset[];
};

export class BrowserImportLimitError extends Error {
  code: string;
  fileName: string;
  actualBytes: number | null;
  limitBytes: number | null;
  limitLabel: string;
  helpMessage: string;

  constructor(
    message: string,
    code = "browser-import-limit",
    details: {
      fileName?: string;
      actualBytes?: number | null;
      limitBytes?: number | null;
      limitLabel?: string;
      helpMessage?: string;
    } = {}
  ) {
    super(message);
    this.name = "BrowserImportLimitError";
    this.code = code;
    this.fileName = details.fileName || "";
    this.actualBytes = details.actualBytes ?? null;
    this.limitBytes = details.limitBytes ?? null;
    this.limitLabel = details.limitLabel || "";
    this.helpMessage = details.helpMessage || MAXIMUM_ASSET_SIZE_MESSAGE;
  }
}

export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) return "unknown size";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
};

const fileDisplayName = (file: File | Blob | null | undefined, fallback = "file"): string => {
  const namedFile = file as File | undefined;
  return String(namedFile?.name || fallback);
};

export const isBrowserImportLimitError = (error: unknown): error is BrowserImportLimitError =>
  error instanceof BrowserImportLimitError ||
  (error as { name?: unknown })?.name === "BrowserImportLimitError";

export const userMessageForImportError = (error: unknown, fallback: string): string => {
  if (isBrowserImportLimitError(error) && error.message) return error.message;
  return fallback;
};

export const browserImportLimitIssueFromError = (
  error: unknown
): BrowserImportLimitIssue | null => {
  if (!isBrowserImportLimitError(error)) return null;
  return {
    code: error.code,
    message: error.message,
    fileName: error.fileName,
    actualBytes: error.actualBytes,
    limitBytes: error.limitBytes,
    actualSizeLabel: error.actualBytes == null ? "" : formatBytes(error.actualBytes),
    limitSizeLabel:
      error.limitLabel || (error.limitBytes == null ? "" : formatBytes(error.limitBytes)),
    helpMessage: error.helpMessage,
  };
};

export const browserImportLimitIssueFromErrors = (
  errors: unknown[]
): BrowserImportLimitIssue | null => {
  const issues = errors
    .map((error) => browserImportLimitIssueFromError(error))
    .filter((issue): issue is BrowserImportLimitIssue => Boolean(issue));
  if (issues.length === 0) return null;
  if (issues.length === 1) return issues[0];

  const firstIssue = issues[0];
  return {
    ...firstIssue,
    message: `${issues.length} assets exceed the maximum size limit.`,
    actualBytes: null,
    actualSizeLabel: "",
    rejectedAssets: issues.map((issue) => ({
      fileName: issue.fileName || "Selected file",
      actualBytes: issue.actualBytes,
      actualSizeLabel: issue.actualSizeLabel,
    })),
  };
};

export const showBrowserImportLimitIssue = (error: unknown): boolean => {
  const issue = browserImportLimitIssueFromError(error);
  if (!issue || typeof window === "undefined") return false;
  window.dispatchEvent(new CustomEvent("critter-import-limit", { detail: issue }));
  return true;
};

export const requestBrowserImportLimitProceedForIssue = (
  issue: BrowserImportLimitIssue | null
): Promise<boolean> => {
  if (!issue || typeof window === "undefined") return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;
    const settle = (value: boolean) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    window.dispatchEvent(
      new CustomEvent("critter-import-limit", {
        detail: {
          ...issue,
          canProceedWithoutRejectedAsset: true,
          onProceedWithoutRejectedAsset: () => settle(true),
          onCancelImport: () => settle(false),
        },
      })
    );
  });
};

export const requestBrowserImportLimitProceed = (error: unknown): Promise<boolean> =>
  requestBrowserImportLimitProceedForIssue(browserImportLimitIssueFromError(error));

export const assertMeshFileWithinBrowserImportLimit = (
  file: File | Blob | null | undefined,
  label?: string
): void => {
  if (!file) return;
  if (file.size <= BROWSER_IMPORT_LIMITS.meshFileBytes) return;
  const name = label || fileDisplayName(file, "mesh");
  throw new BrowserImportLimitError(
    `Mesh "${name}" is ${formatBytes(file.size)}. Browser import supports OBJ/STL/DAE/glTF files up to ${formatBytes(
      BROWSER_IMPORT_LIMITS.meshFileBytes
    )}. ${MAXIMUM_ASSET_SIZE_MESSAGE}`,
    "mesh-file-too-large",
    {
      fileName: name,
      actualBytes: file.size,
      limitBytes: BROWSER_IMPORT_LIMITS.meshFileBytes,
      helpMessage: MAXIMUM_ASSET_SIZE_MESSAGE,
    }
  );
};

export const assertArchiveFileWithinBrowserImportLimit = (file: File | Blob): void => {
  if (file.size <= BROWSER_IMPORT_LIMITS.archiveFileBytes) return;
  throw new BrowserImportLimitError(
    `Project archive "${fileDisplayName(file, "archive")}" is ${formatBytes(
      file.size
    )}. Browser import supports archives up to ${formatBytes(
      BROWSER_IMPORT_LIMITS.archiveFileBytes
    )}. ${MAXIMUM_ASSET_SIZE_MESSAGE}`,
    "archive-file-too-large",
    {
      fileName: fileDisplayName(file, "archive"),
      actualBytes: file.size,
      limitBytes: BROWSER_IMPORT_LIMITS.archiveFileBytes,
      helpMessage: MAXIMUM_ASSET_SIZE_MESSAGE,
    }
  );
};

type ZipMetadata = {
  entries: number;
  files: number;
  totalUncompressedBytes: number;
};

const findEndOfCentralDirectory = (bytes: Uint8Array): number => {
  const minOffset = Math.max(0, bytes.length - 0xffff - 22);
  for (let offset = bytes.length - 22; offset >= minOffset; offset -= 1) {
    if (
      bytes[offset] === 0x50 &&
      bytes[offset + 1] === 0x4b &&
      bytes[offset + 2] === 0x05 &&
      bytes[offset + 3] === 0x06
    ) {
      return offset;
    }
  }
  return -1;
};

const decodeZipName = (bytes: Uint8Array): string => {
  if (typeof TextDecoder === "undefined") {
    return Array.from(bytes, (byte) => String.fromCharCode(byte)).join("");
  }
  return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
};

export const inspectZipArchiveBytes = (bytes: Uint8Array, archiveName = "archive"): ZipMetadata => {
  const eocdOffset = findEndOfCentralDirectory(bytes);
  if (eocdOffset < 0) {
    throw new BrowserImportLimitError(
      `Project archive "${archiveName}" could not be inspected before import.`,
      "archive-metadata-missing"
    );
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const entries = view.getUint16(eocdOffset + 10, true);
  const centralDirectorySize = view.getUint32(eocdOffset + 12, true);
  const centralDirectoryOffset = view.getUint32(eocdOffset + 16, true);
  if (
    entries === 0xffff ||
    centralDirectorySize === 0xffffffff ||
    centralDirectoryOffset === 0xffffffff
  ) {
    throw new BrowserImportLimitError(
      `Project archive "${archiveName}" uses Zip64 metadata, which is not supported by browser import. ${MAXIMUM_ASSET_SIZE_MESSAGE}`,
      "archive-zip64-unsupported",
      { fileName: archiveName, helpMessage: MAXIMUM_ASSET_SIZE_MESSAGE }
    );
  }
  if (entries > BROWSER_IMPORT_LIMITS.archiveEntries) {
    throw new BrowserImportLimitError(
      `Project archive "${archiveName}" contains ${entries} entries. Browser import supports up to ${BROWSER_IMPORT_LIMITS.archiveEntries}.`,
      "archive-too-many-entries",
      {
        fileName: archiveName,
        limitLabel: `${BROWSER_IMPORT_LIMITS.archiveEntries} entries`,
      }
    );
  }

  let offset = centralDirectoryOffset;
  let files = 0;
  let totalUncompressedBytes = 0;
  for (let index = 0; index < entries; index += 1) {
    if (offset + 46 > bytes.length || view.getUint32(offset, true) !== 0x02014b50) {
      throw new BrowserImportLimitError(
        `Project archive "${archiveName}" has invalid central directory metadata.`,
        "archive-metadata-invalid"
      );
    }
    const compressedSize = view.getUint32(offset + 20, true);
    const uncompressedSize = view.getUint32(offset + 24, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const nameStart = offset + 46;
    const nameEnd = nameStart + nameLength;
    const nextOffset = nameEnd + extraLength + commentLength;
    if (
      compressedSize === 0xffffffff ||
      uncompressedSize === 0xffffffff ||
      nameEnd > bytes.length ||
      nextOffset > bytes.length
    ) {
      throw new BrowserImportLimitError(
        `Project archive "${archiveName}" has unsupported or invalid entry metadata.`,
        "archive-entry-metadata-invalid"
      );
    }

    const name = decodeZipName(bytes.subarray(nameStart, nameEnd));
    if (!name.endsWith("/")) {
      files += 1;
      if (!isSafeRelativeArchivePath(name)) {
        throw new BrowserImportLimitError(
          `Project archive "${archiveName}" contains an unsafe file path: ${name}`,
          "archive-unsafe-path"
        );
      }
      if (uncompressedSize > BROWSER_IMPORT_LIMITS.archiveEntryBytes) {
        throw new BrowserImportLimitError(
          `Archive entry "${name}" expands to ${formatBytes(
            uncompressedSize
          )}. Browser import supports entries up to ${formatBytes(
            BROWSER_IMPORT_LIMITS.archiveEntryBytes
          )}. ${MAXIMUM_ASSET_SIZE_MESSAGE}`,
          "archive-entry-too-large",
          {
            fileName: name,
            actualBytes: uncompressedSize,
            limitBytes: BROWSER_IMPORT_LIMITS.archiveEntryBytes,
            helpMessage: MAXIMUM_ASSET_SIZE_MESSAGE,
          }
        );
      }
      totalUncompressedBytes += uncompressedSize;
      if (totalUncompressedBytes > BROWSER_IMPORT_LIMITS.archiveTotalUncompressedBytes) {
        throw new BrowserImportLimitError(
          `Project archive "${archiveName}" expands to at least ${formatBytes(
            totalUncompressedBytes
          )}. Browser import supports archives up to ${formatBytes(
            BROWSER_IMPORT_LIMITS.archiveTotalUncompressedBytes
          )} after extraction. ${MAXIMUM_ASSET_SIZE_MESSAGE}`,
          "archive-uncompressed-too-large",
          {
            fileName: archiveName,
            actualBytes: totalUncompressedBytes,
            limitBytes: BROWSER_IMPORT_LIMITS.archiveTotalUncompressedBytes,
            helpMessage: MAXIMUM_ASSET_SIZE_MESSAGE,
          }
        );
      }
    }
    offset = nextOffset;
  }

  return { entries, files, totalUncompressedBytes };
};

export const readZipFileWithinBrowserImportLimits = async (file: File): Promise<Uint8Array> => {
  assertArchiveFileWithinBrowserImportLimit(file);
  const bytes = new Uint8Array(await file.arrayBuffer());
  inspectZipArchiveBytes(bytes, fileDisplayName(file, "archive"));
  return bytes;
};

export const assertObjTextWithinBrowserImportLimit = (text: string, meshName: string): void => {
  const bytes = new Blob([text]).size;
  if (bytes > BROWSER_IMPORT_LIMITS.objTextBytes) {
    throw new BrowserImportLimitError(
      `OBJ "${meshName}" is ${formatBytes(bytes)} after reading. Browser import supports OBJ text up to ${formatBytes(
        BROWSER_IMPORT_LIMITS.objTextBytes
      )}. ${MAXIMUM_ASSET_SIZE_MESSAGE}`,
      "obj-text-too-large",
      {
        fileName: meshName,
        actualBytes: bytes,
        limitBytes: BROWSER_IMPORT_LIMITS.objTextBytes,
        helpMessage: MAXIMUM_ASSET_SIZE_MESSAGE,
      }
    );
  }

  let vertexLines = 0;
  let faceLines = 0;
  let materialReferenceLines = 0;
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    if (/^\s*v\s+/i.test(line)) vertexLines += 1;
    else if (/^\s*f\s+/i.test(line)) faceLines += 1;
    else if (/^\s*(?:usemtl|mtllib)\s+/i.test(line)) materialReferenceLines += 1;
    if (
      vertexLines > BROWSER_IMPORT_LIMITS.objVertexLines ||
      faceLines > BROWSER_IMPORT_LIMITS.objFaceLines ||
      materialReferenceLines > BROWSER_IMPORT_LIMITS.objMaterialReferenceLines
    ) {
      throw new BrowserImportLimitError(
        `OBJ "${meshName}" is too complex for browser import. ${MAXIMUM_ASSET_SIZE_MESSAGE}`,
        "obj-too-complex"
      );
    }
  }
};
